import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import type { DealWithDetails, Comment, ApprovalAction, DealsListFilter } from '../types';

/**
 * Хук для списка сделок с автообновлением
 */
export function useDeals(filter?: DealsListFilter) {
  const [deals, setDeals] = useState<DealWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const fetchDeals = useCallback(async () => {
    try {
      const data = await api.deals.list(filter);
      setDeals(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setIsLoading(false);
    }
  }, [filter?.actionRequired, filter?.categoryId]);

  useEffect(() => {
    setIsLoading(true);
    fetchDeals();

    // Polling каждые 30 сек
    intervalRef.current = setInterval(fetchDeals, 30000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDeals]);

  return { deals, isLoading, error, refresh: fetchDeals };
}

/**
 * Хук для деталей одной сделки
 */
export function useDeal(dealId: number) {
  const [deal, setDeal] = useState<DealWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeal = useCallback(async () => {
    try {
      const data = await api.deals.get(dealId);
      setDeal(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setIsLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    setIsLoading(true);
    fetchDeal();
  }, [fetchDeal]);

  return { deal, isLoading, error, refresh: fetchDeal };
}

/**
 * Хук для выполнения действий над сделкой
 */
export function useDealAction() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeAction = useCallback(async (
    dealId: number,
    action: ApprovalAction,
    body?: { message?: string; taskType?: string; rating?: number; ratingComment?: string }
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await api.deals.action(dealId, action, body);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка выполнения';
      setError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { executeAction, isLoading, error };
}

/**
 * Хук для комментариев с автообновлением
 */
export function useComments(dealId: number) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const isFirstLoad = useRef(true);

  const fetchComments = useCallback(async () => {
    try {
      const data = await api.deals.getComments(dealId);
      setComments(data);
      setError(null);
      isFirstLoad.current = false;
    } catch (err) {
      // Показываем ошибку только при первой загрузке
      // Polling-ошибки молча игнорируем
      if (isFirstLoad.current) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки комментариев');
        isFirstLoad.current = false;
      }
    } finally {
      setIsLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    isFirstLoad.current = true;
    fetchComments();

    // Polling каждые 15 сек
    intervalRef.current = setInterval(fetchComments, 15000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchComments]);

  const addComment = useCallback(async (text: string) => {
    await api.deals.addComment(dealId, text);
    // Обновляем список после добавления
    await fetchComments();
  }, [dealId, fetchComments]);

  return { comments, isLoading, error, addComment, refresh: fetchComments };
}
